import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TimeoutError, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { OnboardingService } from '../onboarding/onboarding.service';
import { ConfigService } from '../services/config.service';
import { LogglyService } from '../services/loggly.service';

/** Query params of a ServiceNear link that must stay in the URL (interceptor token header, logout back to the app). */
const THIRD_PARTY_PARAMS = ['thirdpartyappurl', 'thirdpartyuserkey'];

/**
 * The page stays blank while this guard waits, so it never waits longer than this - a check that does not
 * answer (e.g. an expired session stuck on token refresh) sends the seller to the login page instead.
 */
const CHECK_TIMEOUT_MS = 12000;

/**
 * On the /dashboard and /servicenear routes: a seller with unfinished app onboarding (ServiceNear) goes straight
 * to /onboarding, so the dashboard (and all its API calls) never loads first. /servicenear covers the direct
 * ServiceNear links (/servicenear/dashboard?thirdpartyappurl=&thirdpartyuserkey=), which the login page follows
 * as its redirect. This is the only place that redirects to /onboarding - the dashboard components do not.
 */
export const pendingOnboardingGuard: CanActivateFn = (route, state) => {
  const onboardingService = inject(OnboardingService);
  const configService = inject(ConfigService);
  const logglyService = inject(LogglyService);
  const router = inject(Router);

  const loginDetails = JSON.parse(localStorage.getItem('admin_Details') ?? 'null');
  // not logged in: AuthGuard handles it; superadmin (/servicenear/superadmin) has no onboarding
  if (!loginDetails?.UserID || loginDetails?.UserType === 'superadmin' || onboardingService.isMarkedComplete()) {
    return of(true);
  }

  // keep thirdpartyappurl / thirdpartyuserkey on /onboarding - the blocked page never finishes loading,
  // so AppComponent.carryQueryParams has not seen them yet
  const requested = router.parseUrl(state.url).queryParams;
  const queryParams: Record<string, string> = {};
  THIRD_PARTY_PARAMS.filter(key => requested[key]).forEach(key => queryParams[key] = requested[key]);

  return onboardingService.checkOnboarding().pipe(
    timeout(CHECK_TIMEOUT_MS),
    // 'unknown' (API failed) lets the seller in - the check runs again on the next visit
    map(check => check.state === 'pending' ? router.createUrlTree(['/onboarding'], { queryParams }) : true),
    catchError(err => {
      if (err instanceof TimeoutError) {
        // no white screen: fresh login (with a ServiceNear key in the URL the login page signs in again by itself)
        logglyService.log('pendingOnboardingGuard - onboarding check timed out, sending to login: ' + state.url);
        configService.logoutUser();
        return of(false);
      }
      return of(true);
    })
  );
};
